// Work mode's view model (M2-07): API task cards → the work views' `TaskSummary`, status tones,
// priorities, dates, and the seats behind assignees. Assignees are seats (06): a person's seat
// reads as its title, a DE's seat as the DE's name with the agent mark. When a task has both a
// person's seat and a DE's seat, the person stays the accountable assignee and the DE is the
// delegate doing the work (the Linear pattern, research 2026-10-20 §10): the drawer shows them
// as Assignee and Delegate, cards show the person first. Pure apart from `useSeatPeople`.
import type { StatusTone, TaskPerson, TaskPriority, TaskSummary } from "@companyos/ui";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import type { Api } from "../../api/client";
import { orgChartQuery } from "../chart/chart-queries";
import { digitalEmployeesQuery, peopleQuery, seatsQuery } from "../queries";
import { useSession } from "../use-feature";
import type { TagColor } from "./tag-logic";
import type { Board, StatusDef, TaskCard } from "./view-engine";

const TONES: readonly StatusTone[] = ["neutral", "accent", "success", "warning", "danger", "info"];

/** A status's tone: its colour when it names a tone, else done → success, started → accent. */
export function statusTone(s: StatusDef | undefined): StatusTone {
  if (s?.color && (TONES as readonly string[]).includes(s.color)) return s.color as StatusTone;
  if (s?.done) return "success";
  if (s?.started) return "accent";
  return "neutral";
}

/** `task.priority`: 0 none, 1 urgent … 4 low. */
export const PRIORITIES: { value: number; key: TaskPriority | null; label: string }[] = [
  { value: 1, key: "urgent", label: "Urgent" },
  { value: 2, key: "high", label: "High" },
  { value: 3, key: "medium", label: "Medium" },
  { value: 4, key: "low", label: "Low" },
  { value: 0, key: null, label: "No priority" },
];

export function priorityKey(p: number): TaskPriority | undefined {
  return PRIORITIES.find((x) => x.value === p)?.key ?? undefined;
}

/** The local calendar day of an instant ("YYYY-MM-DD"). */
export function localDay(iso: string | Date): string {
  const d = typeof iso === "string" ? new Date(iso) : iso;
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** A local day as the instant the API stores for a due date (17:00 local). */
export function dueInstant(day: string): string {
  const [y, m, d] = day.split("-").map(Number);
  return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1, 17, 0, 0).toISOString();
}

export function startInstant(day: string): string {
  const [y, m, d] = day.split("-").map(Number);
  return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1, 9, 0, 0).toISOString();
}

export type SeatPerson = TaskPerson & {
  seatId: string;
  /** The seat's title ("Recruiting lead"), under the holder's name; "Open seat" when nobody. */
  hint: string;
  /** The seat's title. */
  seat: string;
  /** Short form for a row or a column: "Dana W.", the DE's name, or "Recruiting lead (open)". */
  label: string;
  /** "Dana Whitfield · Recruiting lead", the DE's name, or "Recruiting lead (open)". */
  full: string;
  de: boolean;
  /** Nobody holds the seat (`name` is then the seat's title). */
  open: boolean;
  closed: boolean;
};

/** "Dana Whitfield" → "Dana W."; one word stays as it is. */
export function shortName(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  const first = words[0] ?? name;
  const last = words.length > 1 ? words[words.length - 1] : undefined;
  return last?.[0] ? `${first} ${last[0].toUpperCase()}.` : first;
}

export type SeatHolder = {
  membership_id: string;
  /** "person", "digital_employee" or "personal_agent"; only a person names the seat. */
  membership_kind: string;
  display_name: string;
};

export type SeatPeople = {
  /** Every seat as a person or a DE (closed seats included, so old tasks still read). */
  all: SeatPerson[];
  of: (seatId: string) => SeatPerson;
  /** Membership id → display name (message authors). */
  nameOf: (membershipId: string | null) => { name: string; kind: "person" | "agent" };
  /** WORK-OS: the seats the caller holds (quick add on My tasks assigns the first). */
  mine: string[];
  ready: boolean;
};

/**
 * Seats as the people on them (people think in people, not seats): a seat a person holds reads
 * as that person ("Dana W.", "Dana Whitfield · Recruiting lead"), a seat a DE holds as the DE, an
 * open seat as its title with "(open)". `holders` is the org chart's per-seat list; without it a
 * filled seat still reads as its title.
 */
export function seatPeople(
  seats: readonly { id: string; title: string; status: string }[],
  des: readonly { membership_id: string; display_name: string; seat: { id: string } | null }[],
  people: readonly { id: string; display_name: string }[] = [],
  holders: ReadonlyMap<string, readonly SeatHolder[]> = new Map(),
  /** The caller's membership in the company: its held seats become `mine`. */
  membershipId: string | null = null,
): Omit<SeatPeople, "ready"> {
  const deOn = new Map(des.flatMap((d) => (d.seat ? [[d.seat.id, d] as const] : [])));
  const all = seats.map((s): SeatPerson => {
    const closed = s.status === "closed";
    const de = deOn.get(s.id);
    if (de)
      return {
        seatId: s.id,
        name: de.display_name,
        kind: "agent",
        hint: s.title,
        seat: s.title,
        label: de.display_name,
        full: de.display_name,
        de: true,
        open: false,
        closed,
      };
    const holder = (holders.get(s.id) ?? []).find((h) => h.membership_kind === "person");
    if (holder)
      return {
        seatId: s.id,
        name: holder.display_name,
        kind: "person",
        hint: s.title,
        seat: s.title,
        label: shortName(holder.display_name),
        full: `${holder.display_name} · ${s.title}`,
        de: false,
        open: false,
        closed,
      };
    const open = s.status === "open";
    // A seat titled "… (open)" (the demo seed's) is not suffixed twice.
    const openLabel = /\(open\)$/i.test(s.title) ? s.title : `${s.title} (open)`;
    return {
      seatId: s.id,
      name: s.title,
      kind: "person",
      hint: open ? "Open seat" : "Seat",
      seat: s.title,
      label: open ? openLabel : s.title,
      full: open ? openLabel : s.title,
      de: false,
      open,
      closed,
    };
  });
  const byId = new Map(all.map((p) => [p.seatId, p]));
  const names = new Map<string, { name: string; kind: "person" | "agent" }>([
    ...people.map((p) => [p.id, { name: p.display_name, kind: "person" as const }] as const),
    ...des.map((d) => [d.membership_id, { name: d.display_name, kind: "agent" as const }] as const),
  ]);
  const mine = membershipId
    ? seats
        .filter((s) => (holders.get(s.id) ?? []).some((h) => h.membership_id === membershipId))
        .map((s) => s.id)
    : [];
  return {
    all,
    mine,
    of: (id) =>
      byId.get(id) ?? {
        seatId: id,
        name: "Unknown seat",
        kind: "person",
        hint: "",
        seat: "Unknown seat",
        label: "Unknown seat",
        full: "Unknown seat",
        de: false,
        open: false,
        closed: false,
      },
    nameOf: (m) =>
      m
        ? (names.get(m) ?? { name: "Someone", kind: "person" })
        : { name: "CompanyOS", kind: "agent" },
  };
}

/**
 * Seats, DEs, people (for names) and the org chart (who holds each seat), cached across Work:
 * four calls the first time, shared with Chart mode.
 */
export function useSeatPeople(api: Api, co: string): SeatPeople {
  const seats = useInfiniteQuery(seatsQuery(api, co));
  const des = useInfiniteQuery(digitalEmployeesQuery(api, co));
  const people = useInfiniteQuery(peopleQuery(api, co));
  const chart = useQuery(orgChartQuery(api, co));
  const { me } = useSession();
  const membershipId = me.memberships.find((m) => m.company_id === co)?.membership_id ?? null;
  const s = seats.data?.pages.flatMap((p) => p.data);
  const d = des.data?.pages.flatMap((p) => p.data);
  const pp = people.data?.pages.flatMap((p) => p.data);
  const chartSeats = chart.data?.seats;
  return useMemo(() => {
    const holders = new Map<string, readonly SeatHolder[]>(
      (chartSeats ?? []).map((cs) => [cs.id, cs.holders] as const),
    );
    return {
      ...seatPeople(s ?? [], d ?? [], pp ?? [], holders, membershipId),
      ready: Boolean(s && d),
    };
  }, [s, d, pp, chartSeats, membershipId]);
}

// WORK-OS: the overview's board health and My tasks' sections --------------------------------

export type BoardHealth = {
  key: "empty" | "complete" | "on_track" | "at_risk" | "off_track";
  label: string;
  tone: StatusTone;
};

/**
 * A board's health chip (ClickUp's On track / At risk / Off track): nothing to do, or all of it
 * done; off track when at least two tasks and a quarter of the open work are overdue; at risk
 * when a tenth of it is overdue, or a third of it waits on unfinished hard dependencies; else
 * on track. One late task in a dozen is ordinary (wave 7: the first rule called any overdue or
 * blocked task a risk, so every board read "At risk"); blocked alone is ordinary sequencing.
 */
export function boardHealth(s: {
  total: number;
  open: number;
  overdue: number;
  blocked: number;
}): BoardHealth {
  if (s.total === 0) return { key: "empty", label: "No tasks", tone: "neutral" };
  if (s.open === 0) return { key: "complete", label: "Done", tone: "success" };
  if (s.overdue >= 2 && s.overdue * 4 >= s.open)
    return { key: "off_track", label: "Off track", tone: "danger" };
  if ((s.overdue > 0 && s.overdue * 10 >= s.open) || (s.blocked > 0 && s.blocked * 3 >= s.open))
    return { key: "at_risk", label: "At risk", tone: "warning" };
  return { key: "on_track", label: "On track", tone: "success" };
}

/** My tasks' sections (Asana's): the day decides, from the local calendar. */
export type MyBucket = "overdue" | "today" | "week" | "later" | "none";

export const MY_BUCKETS: { id: MyBucket; title: string }[] = [
  { id: "today", title: "Today" },
  { id: "overdue", title: "Overdue" },
  { id: "week", title: "This week" },
  { id: "later", title: "Later" },
  { id: "none", title: "No date" },
];

/** A day `n` days after `day` ("YYYY-MM-DD"). */
export function addDays(day: string, n: number): string {
  const [y, m, d] = day.split("-").map(Number);
  return localDay(new Date(y ?? 1970, (m ?? 1) - 1, (d ?? 1) + n));
}

/** Which section a due day falls in: this week runs from tomorrow to six days on. */
export function myBucket(due: string | null, today: string): MyBucket {
  if (!due) return "none";
  const d = due.slice(0, 10);
  if (d < today) return "overdue";
  if (d === today) return "today";
  if (d <= addDays(today, 6)) return "week";
  return "later";
}

/** The due day a task quick-added in a section gets (none for Overdue and No date). */
export function bucketDue(bucket: MyBucket, today: string): string | undefined {
  if (bucket === "today") return today;
  if (bucket === "week") return addDays(today, 3);
  if (bucket === "later") return addDays(today, 14);
  return undefined;
}

/** "Oct 23": the short local date of a day or an instant, for the overview's columns. */
export function shortDay(dayOrIso: string): string {
  const day = dayOrIso.length > 10 ? localDay(dayOrIso) : dayOrIso;
  const [y, m, d] = day.split("-").map(Number);
  return new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" }).format(
    new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1),
  );
}

/**
 * Per task, how many of its subtasks (cards with it as `parent_task_id`) are done: computed
 * over the cards on screen (the board's), for the subtask counter on cards and rows.
 */
export function subtaskCounts(
  cards: readonly Pick<TaskCard, "id" | "parent_task_id" | "status" | "completed_at">[],
  board: Pick<Board, "status_set"> | undefined,
): Map<string, { done: number; total: number }> {
  const out = new Map<string, { done: number; total: number }>();
  for (const c of cards) {
    if (!c.parent_task_id) continue;
    const n = out.get(c.parent_task_id) ?? { done: 0, total: 0 };
    n.total++;
    if (isDone(c, board)) n.done++;
    out.set(c.parent_task_id, n);
  }
  return out;
}

/** The accountable people (person seats) and the delegates (DE seats) of a task. */
export function splitAssignees(seatIds: readonly string[], people: Pick<SeatPeople, "of">) {
  const all = seatIds.map((id) => people.of(id));
  return { owners: all.filter((p) => !p.de), delegates: all.filter((p) => p.de) };
}

export type SummaryContext = {
  board: Pick<Board, "status_set" | "name"> | undefined;
  people: Pick<SeatPeople, "of">;
  today: string;
  /** "Recruiting › Hiring pipeline" for cards outside their board. */
  location?: string;
  /** Select fields shown as chips (first two). */
  chips?: { id: string; choices: { key: string; name: string }[] }[];
  /** WORK-OS: subtask counts per parent task (`subtaskCounts`). */
  subtasks?: ReadonlyMap<string, { done: number; total: number }>;
  /** WOS-BUNDLE: the company's tags by id (chips in their colours, before the field chips). */
  tags?: ReadonlyMap<string, { name: string; color: TagColor }>;
};

export function isDone(
  card: Pick<TaskCard, "status" | "completed_at">,
  board: Pick<Board, "status_set"> | undefined,
): boolean {
  const s = board?.status_set.find((x) => x.key === card.status);
  return s ? s.done : card.completed_at !== null;
}

export function toSummary(card: TaskCard, ctx: SummaryContext): TaskSummary {
  const status = ctx.board?.status_set.find((s) => s.key === card.status);
  const done = isDone(card, ctx.board);
  const { owners, delegates } = splitAssignees(card.assignee_seat_ids, ctx.people);
  const due = card.due_at ? localDay(card.due_at) : undefined;
  const priority = priorityKey(card.priority);
  const counts = countsOf(card, ctx);
  const tagLabels = (card.tag_ids ?? []).flatMap((id) => {
    const t = ctx.tags?.get(id);
    return t ? [{ label: t.name, tag: t.color }] : [];
  });
  const labels = [
    ...tagLabels,
    ...(ctx.chips ?? []).flatMap((f) => {
      const v = card.fields[f.id];
      const c = f.choices.find((x) => x.key === v);
      return c ? [{ label: c.name, tone: "neutral" as const }] : [];
    }),
  ];
  return {
    id: card.id,
    title: card.title,
    status: {
      label: status?.name ?? card.status,
      tone: statusTone(status),
      kind: done ? "done" : card.blocked ? "blocked" : status?.started ? "active" : "todo",
    },
    done,
    assignees: [...owners, ...delegates].map((p) => ({
      name: p.name,
      kind: p.kind ?? "person",
      label: p.label,
      title: p.full,
    })),
    ...(card.start_at ? { start: localDay(card.start_at) } : {}),
    ...(due ? { due, overdue: !done && due < ctx.today } : {}),
    ...(priority ? { priority } : {}),
    ...(labels.length ? { labels: labels.slice(0, tagLabels.length ? 3 : 2) } : {}),
    ...(counts ? { counts } : {}),
    ...(ctx.location ? { location: ctx.location } : {}),
    // TASK-LOCK: an agent run is working on it (greyed, badge, no drag).
    ...(card.agent_lock
      ? {
          locked: {
            by: card.agent_lock.name,
            since: new Date(card.agent_lock.since).toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            }),
          },
        }
      : {}),
  };
}

/** The card's counters: its subtasks (when known) and its checklist, each only when it has any. */
function countsOf(card: TaskCard, ctx: SummaryContext) {
  const subtasks = ctx.subtasks?.get(card.id);
  const out = {
    ...(subtasks?.total ? { subtasks } : {}),
    ...(card.checklist.total ? { checklist: card.checklist } : {}),
  };
  return Object.keys(out).length ? out : undefined;
}

/** The first done status of a board (what "Complete" sets), and the first open one. */
export function doneStatus(board: Pick<Board, "status_set"> | undefined): string | undefined {
  return board?.status_set.find((s) => s.done)?.key;
}
export function openStatus(board: Pick<Board, "status_set"> | undefined): string | undefined {
  return board?.status_set.find((s) => !s.done)?.key;
}
/** The status after this one in board order ("Move to …"). */
export function nextStatus(
  board: Pick<Board, "status_set"> | undefined,
  current: string,
): StatusDef | undefined {
  const list = board?.status_set ?? [];
  const i = list.findIndex((s) => s.key === current);
  return i >= 0 ? list[i + 1] : undefined;
}

/** "#12" */
export const taskRef = (t: Pick<TaskCard, "number">) => `#${t.number}`;
